import { Attrs, EventListeners, Minze, MinzeElement, Reactive } from 'minze'
import { getFromSessionCache } from '../../modules/cache'
import { Callback } from '../../modules/callback'
import { Locale } from '../../modules/locale'
import MainModule from '../../modules/main'
import { Routing } from '../../modules/routing'
import { delay, escapeHtml, unescapeHtml } from '../../modules/utils'

const GENDER_SELECT_VALUES = ['notSpecified', 'male', 'female', 'other'].join(',')
const DEFAULT_GENDER_VALUE = 'notSpecified'
const COMMUNICATION_LANGUAGE_SELECT_VALUES = ['fr', 'en'].join(',')
const DEFAULT_COMMUNICATION_LANGUAGE_VALUE = 'fr'
const ZIPCODE_MASK = 'A#A #A#'

Locale.getInstance().registerKeySet('fr', 'lvcAccountInfo', {
  title: 'Mes infos personnelles',
  firstName: 'Prénom',
  lastName: 'Nom',
  gender: 'Genre',
  writeHere: 'Écrivez ici...',
  dateOfBirth: 'Date de naissance',
  dateOfBirthPlaceholder: 'MM/JJ/AAAA',
  dateOfBirthFormat: 'MM/DD/YYYY',
  zipcode: 'Code postal',
  zipcodeMessage:
    'En renseignant votre code postal et en sélectionnant une distance, nous pourrons vous proposer des sorties près de chez vous, dans la zone définie. Ces informations nous permettent de personnaliser vos recommandations et de vous aider à découvrir des activités adaptées à votre emplacement.',
  location: 'Lieu de résidence',
  radius: "Afficher les activités à l'intérieur de cette distance de mon lieu de résidence",
  communicationLanguage: 'Langue de communication',
  languageSelectLabels: 'Français,Anglais',
  save: 'Mettre à jour mes infos',
  genderSelectLabels: 'Non précisé,Homme,Femme,Autre',
  errors: {
    zipcodeLength: 'Le code postal doit contenir 3 ou 6 caractères',
    shortZipcodeLength: 'Le code postal doit contenir 3 caractères',
    firstName: 'Le prénom est obligatoire',
    lastName: 'Le nom est obligatoire',
  },
  dataSavedNotification: 'Modifications sauvegardés',
})

Locale.getInstance().registerKeySet('en', 'lvcAccountInfo', {
  title: 'My personal information',
  firstName: 'First name',
  lastName: 'Last name',
  writeHere: 'Write here...',
  gender: 'Gender',
  dateOfBirth: 'Date of birth',
  dateOfBirthPlaceholder: 'MM/DD/YYYY',
  dateOfBirthFormat: 'MM/DD/YYYY',
  zipcode: 'Postal code',
  zipcodeMessage:
    'By entering your postal code and selecting a distance, we can suggest events near you, within the defined area. This information allows us to personalize your recommendations and help you discover activities tailored to your location.',
  location: 'Place of residence',
  radius: 'Show activities within this distance from my place of residence',
  communicationLanguage: 'Language of communication',
  languageSelectLabels: 'French,English',
  save: 'Update my information',
  genderSelectLabels: 'Not specified,Male,Female,Other',
  errors: {
    zipcodeLength: 'The zipcode must contain 3 or 6 characters',
    shortZipcodeLength: 'The zipcode must contain 3 characters',
    firstName: 'First name is mandatory',
    lastName: 'Last name is mandatory',
  },
  dataSavedNotification: 'Changes saved',
})

export interface LvcAccountInfo {
  titleSeparator: boolean
  fields: string
  submitButton: boolean
  firstName: string
  lastName: string
  gender: string
  dob: string
  zipcode: string
  location: string
  region: string
}

export class LvcAccountInfo extends MinzeElement {
  attrs: Attrs = [
    ['title-separator', false],
    ['fields', 'firstName,lastName,zipcode,location,dob,gender,language'],
    ['submit-button', true],
  ]
  static observedAttributes = ['titleSeparator', 'type', 'submitButton']

  reactive: Reactive = [
    ['firstName', ''],
    ['lastName', ''],
    ['gender', ''],
    ['dob', ''],
    ['zipcode', ''],
    ['location', ''],
    ['region', ''],
    ['radius', 10],
  ]

  errors: any = {
    zipcode: false,
    firstName: false,
    lastName: false,
  }

  private onChangeCb: string = ''
  private onRadiusChangeCb: string = ''
  private onZipcodeChangeCb: string = ''

  onChange() {
    // Get values of all the inputs
    const inputIds = ['firstName', 'lastName', 'gender', 'dob', 'zipcode', 'location']
    const values = inputIds.reduce(
      (acc, id) => {
        const element = this.select(`[name="${id}"]`) as HTMLInputElement
        acc[id] = element?.value || ''
        return acc
      },
      {} as { [key: string]: string }
    )

    // Check if there are any non-empty values
    const hasValues = Object.values(values).some(value => value.trim() !== '')

    // Set the page to dirty or clearDirty accordingly
    const mainModule = MainModule.getInstance()
    const routing = mainModule.getRouting()

    const isDirty = hasValues && inputIds.some(key => this[key] !== values[key])

    if (isDirty) {
      routing.setDirty()
    } else {
      routing.clearDirty()
    }
  }

  async onReady(): Promise<void> {
    const mainModule = MainModule.getInstance()
    const userData = mainModule.getAuth().getUserData()

    if (userData && userData.profile) {
      const personalInfo = userData.profile.personalData
      this.firstName = personalInfo.firstName
      this.lastName = personalInfo.lastName
      this.language = personalInfo.lang ?? 'fr'
      this.dob = personalInfo.dob || ''
      this.gender = personalInfo.gender
      this.zipcode = personalInfo.zipcode
      this.location = personalInfo.location
      this.region = personalInfo.region
    }

    this.onChangeCb = Callback.attach(this.onChange.bind(this))
    this.onRadiusChangeCb = Callback.attach(this.onRadiusChange.bind(this))
    this.onZipcodeChangeCb = Callback.attach(this.onZipcodeChange.bind(this))
    this.rerender()
  }

  handleAuthChanged = (event: Event) => {
    const detail = (event as CustomEvent<any>).detail

    const userData = detail.user
    if (userData) {
      const mainModule = MainModule.getInstance()

      let ud = mainModule.getAuth().getUserData()
      const personalInfo = ud.profile.personalData

      this.firstName = personalInfo.firstName
      this.lastName = personalInfo.lastName
      this.language = personalInfo.lang ?? DEFAULT_COMMUNICATION_LANGUAGE_VALUE
      this.dob = personalInfo.dob || ''
      this.zipcode = personalInfo.zipcode
      this.location = personalInfo.location
      this.radius = personalInfo.radius ?? 10
      this.region = personalInfo.region
      this.gender = personalInfo.gender ?? DEFAULT_GENDER_VALUE
    }

    this.rerender()

    this.setLocation(this.zipcode as string)
  }

  onDateChange = async (event: Event) => {
    this.dob = (event as any).detail.value
    await delay(0)
    this.rerender()
  }

  onRadiusChange = (event: CustomEvent) => {
    this.radius = event // the radius is in km
  }

  save = async (e: CustomEvent) => {
    for (const key in this.errors) {
      this.errors[key] = false
    }

    const mainModule = MainModule.getInstance()
    const firstNameElement: any = this.select('#firstName')
    const firstName = unescapeHtml(firstNameElement?.value || '')
    const lastNameElement: any = this.select('#lastName')
    const lastName = unescapeHtml(lastNameElement?.value || '')
    const langElement: any = this.select('#language')
    const language = langElement?.value
    const dobElement: any = this.select('[name="dob"]')
    const dob = dobElement?.value
    const zipcodeElement: any = this.select('[name="zipcode"]')
    const zipcode = unescapeHtml(zipcodeElement?.value || '')
    const genderElement: any = this.select('[name="gender"]')
    const gender = genderElement?.value

    this.firstName = firstName
    this.lastName = lastName
    this.language = language
    this.dob = dob
    this.zipcode = zipcode.toUpperCase().trim()
    this.gender = gender
    this.errors.zipcode = false
    let zip = this.zipcode as string

    if (
      this.fields.includes('zipcode') &&
      zip &&
      zip.length > 0 &&
      zip.length !== 3 &&
      zip.length !== 7
    ) {
      this.errors.zipcode = Locale.getKey('lvcAccountInfo.errors.zipcodeLength')
      this.rerender()
    } else if ((this.fields.includes('zipcode') && !zip) || zip.length === 0) {
      this.errors.zipcode = Locale.getKey('lvcAccountInfo.errors.zipcodeLength')
      this.rerender()
    }

    if ((this.fields.includes('firstName') && !firstName) || firstName.length === 0) {
      this.errors.firstName = Locale.getKey('lvcAccountInfo.errors.firstName')
    }

    if ((this.fields.includes('lastName') && !lastName) || lastName.length === 0) {
      this.errors.lastName = Locale.getKey('lvcAccountInfo.errors.lastName')
    }

    let hasErrors = false
    for (let key in this.errors) {
      if (this.errors[key]) {
        hasErrors = true
        break
      }
    }

    if (hasErrors) {
      this.rerender()
      return
    }

    const profileObject: any = {
      firstName: this.firstName,
      lastName: this.lastName,
      lang: this.language ?? 'fr',
      dob: this.dob,
      zipcode: this.zipcode,
      location: this.location,
      radius: this.radius,
      region: this.region,
      gender: this.gender,
    }

    for (let key in profileObject) {
      if (profileObject[key] === undefined) {
        delete profileObject[key]
      }
    }
    await mainModule.getAuth().saveProfile('profile-personal', profileObject)
    if (e?.detail?.isNewAccount) {
      await mainModule.getAuth().saveProfile('user-process', { signupCompleted: true })

      let providers = await mainModule.getAuth().getFirebaseAuth().currentUser.providerData

      // here we track the method used to signup and send the event to GA4
      for (let profile of providers) {
        if (profile.providerId === 'password') {
          await mainModule.analyticsTrack('compte_complete', {
            method: 'Email',
          })
        } else if (profile.providerId === 'google.com') {
          await mainModule.analyticsTrack('compte_complete', {
            method: 'Google',
          })
        } else if (profile.providerId === 'facebook.com') {
          await mainModule.analyticsTrack('compte_complete', {
            method: 'Facebook',
          })
        }
      }

      const routing = mainModule.getRouting()
      const lang = mainModule.getLang()
      routing.clearDirty()

      //check if the user has the partnerSignupProcessTriggered flag in the session cache
      const partnerSignupProcessTriggered = getFromSessionCache(
        'partnerSignupProcessTriggered',
        true
      )
      if (partnerSignupProcessTriggered) {
        routing.goTo(Routing.ROUTES[mainModule.getLang()].partnerRegister)
      } else {
        routing.goTo(`${Routing.ROUTES[lang].profile}?profile-completed`)
      }

      try {
        // @ts-ignore
        fbq('trackSingle', '1241312297089873', 'SubmitApplication')
      } catch (e) {}
    } else {
      this.rerender()
      mainModule.showToastAlert(Locale.getKey('lvcAccountInfo.dataSavedNotification'))
      mainModule.getRouting().clearDirty()
      Minze.cast('lvc:account-info-saved')
    }
  }

  // function that gets called when the user skips the profile completion
  async skipProfileCompletion() {
    const mainModule = MainModule.getInstance()
    await mainModule.getAuth().saveProfile('user-process', {
      signupCompleted: false,
      signupCompletionSkipped: true,
    })

    await mainModule.analyticsTrack('compte_complete_skipped', {}) // track the event in GA4, because i know we will need it

    const routing = mainModule.getRouting()
    const lang = mainModule.getLang()
    routing.clearDirty()

    //check if the user has the partnerSignupProcessTriggered flag in the session cache
    const partnerSignupProcessTriggered = getFromSessionCache('partnerSignupProcessTriggered', true)

    // if he does, redirect him to the partner registration page
    if (partnerSignupProcessTriggered) {
      routing.goTo(Routing.ROUTES[mainModule.getLang()].partnerRegister)
    } else {
      routing.goTo(`${Routing.ROUTES[lang].profile}?profile-completed`)
    }
  }

  async onZipcodeChange(value: string): Promise<void> {
    this.onChange()
    await this.setLocation(value?.trim()?.length === 3 || value?.trim()?.length === 7 ? value : '')
  }

  async setLocation(zipcode?: string) {
    let location
    if (zipcode) {
      location = await MainModule.getInstance().getGeo().getNearestLocationByPostalCode(zipcode)
    }

    if (location) {
      this.location = location.name
      this.region = location.region
      this.rerender()
    } else {
      this.location = ''
      this.region = ''
      this.rerender()
    }
  }

  html = () => `
    <div class="account-info">
      <lvc-account-header
        label="${Locale.getKey('lvcAccountInfo.title')}"
        separator="${this.titleSeparator}"
      >
        <lvc-account-lock-icon width="44" height="44" slot="icon"></lvc-account-lock-icon>
      </lvc-account-header>
      <div class="account-info-form">
        <div class="account-info-form-row">
          ${
            this.fields.includes('firstName')
              ? /* html */ `
            <div class="account-info-form-col-1/2">
              <lvc-input id="firstName" name="firstName" error="${this.errors.firstName}" placeholder="${Locale.getKey('lvcAccountInfo.writeHere')}" placeholder-color="rgba(30, 0, 125, 0.5)" inline-style="input {color:#1E007D; font-weight:700;}" value="${escapeHtml(this.firstName as string)}" default-value="${escapeHtml(this.firstName as string)}" label="${Locale.getKey('lvcAccountInfo.firstName')}" on-change="${this.onChangeCb}"></lvc-input>
            </div>
          `
              : ''
          }
          ${
            this.fields.includes('lastName')
              ? /* html */ `
            <div class="account-info-form-col-1/2">
              <lvc-input id="lastName" name="lastName" error="${this.errors.lastName}" placeholder="${Locale.getKey('lvcAccountInfo.writeHere')}" value="${escapeHtml(this.lastName as string)}" default-value="${escapeHtml(this.lastName as string)}" label="${Locale.getKey('lvcAccountInfo.lastName')}" on-change="${this.onChangeCb}"></lvc-input>
            </div>
          `
              : ''
          }
        </div>
        <div class="account-info-form-row">
          ${
            this.fields.includes('zipcode')
              ? /* html */ `
            <div class="account-info-form-col-1/3">
              <lvc-input id="address" mask="${ZIPCODE_MASK}" error="${this.errors.zipcode}" name="zipcode" placeholder="ex: A1A" value="${escapeHtml((this.zipcode as string) || '')}" default-value="${escapeHtml((this.zipcode as string) || '')}" label="${Locale.getKey('lvcAccountInfo.zipcode')}" on-change="${this.onZipcodeChangeCb}"></lvc-input>
            </div>
          `
              : ''
          }
          ${
            this.fields.includes('location')
              ? /* html */ `
            <div class="account-info-form-col-2/3">
              <lvc-input id="location" name="location" readonly="true" value="${escapeHtml((this.location as string) || '')}" default-value="${escapeHtml((this.location as string) || '')}" label="${Locale.getKey('lvcAccountInfo.location')}"></lvc-input>
            </div>
          `
              : ''
          }

        </div>

        <div class="account-info-form-row">
          <div class="radius-container">
            <label for="radius" class="label">${Locale.getKey('lvcAccountInfo.radius')}</label>
            <lvc-radius-profile data-callback="${this.onRadiusChangeCb}" default-selected-radius="${escapeHtml((this.radius as string) || '10')}"></lvc-radius-profile>
          </div>
        </div>

        <div class="account-info-form-row">
          <lvc-text font-preset="small">
            ${Locale.getKey('lvcAccountInfo.zipcodeMessage')}
          </lvc-text>
        </div>

        <div class="account-info-form-row">
          ${
            this.fields.includes('dob')
              ? /* html */ `
            <div class="account-info-form-col-1/3">
              <lvc-datepicker id="dob" name="dob" default-value="${this.dob}" value="${this.dob}" label="${Locale.getKey('lvcAccountInfo.dateOfBirth')}" onChange="${this.onChangeCb}"></lvc-datepicker>
            </div>
          `
              : ''
          }
          ${
            this.fields.includes('gender')
              ? /* html */ `
            <div class="account-info-form-col-1/3">
              <lvc-select id="gender" name="gender" placeholder="" default-value="${this.gender}" label="${Locale.getKey('lvcAccountInfo.gender')}" selectOptions="${GENDER_SELECT_VALUES}" selectOptionLabels="${Locale.getKey('lvcAccountInfo.genderSelectLabels')}" onChange="${this.onChangeCb}"></lvc-select>
            </div>
          `
              : ''
          }
          ${
            this.fields.includes('language')
              ? /* html */ `
            <div class="account-info-form-col-1/3">
              <lvc-select id="language" name="language" default-value="${this.language}" label="${Locale.getKey('lvcAccountInfo.communicationLanguage')}" selectOptions="${COMMUNICATION_LANGUAGE_SELECT_VALUES}" selectOptionLabels="${Locale.getKey('lvcAccountInfo.languageSelectLabels')}" onChange="${this.onChangeCb}"></lvc-select>
            </div>
          `
              : ''
          }
        </div>

        ${
          this.submitButton
            ? /* html */ `
          <div class="account-info-form-footer">
            <lvc-button display="flex" align-items="center" justify-content="center" width="100%" padding="15px 25px" id="btn-save">
              ${Locale.getKey('lvcAccountInfo.save')}
              <lvc-chevron-right-icon></lvc-chevron-right-icon>
            </lvc-button>
          </div>
        `
            : ''
        }
      </div>
    </div>
  `

  eventListeners: EventListeners = [
    [window, 'lvc:user_state_changed', this.handleAuthChanged.bind(this)],
    [window, 'lvc:validate-profile-completion', e => this.save(e as CustomEvent)],
    ['#btn-save', 'click', e => this.save(e as CustomEvent)],
    ['[name="dob"]', 'change', this.onDateChange.bind(this)],
    [window, 'lvc:skip-profile-completion', this.skipProfileCompletion.bind(this)],
  ]

  css = () => `
    :host {}

    .account-info-form {
      display: flex;
      flex-direction: column;
      gap: 15px;
    }

    .account-info-form-row {
      display: flex;
      flex-direction: column;
      gap: 15px;
    }

    .radius-container {
      width: 100%;
    }

    .label {
      font-family: inherit;
      color: #1E007D;
      font-size: 14px;
      line-height: 17px;
      font-weight: 700;
      margin-bottom: 4px;
    }

    .account-info-form-footer {
      display: flex;
      justify-content: end;
    }

    lvc-chevron-right-icon {
      width: 24px;
      height: 24px;
      margin-left: 5px;
    }

    ${['1/2', '1/3', '2/3', '1/4', '3/4']
      .map(colSize => {
        const split = colSize.split('/')
        const percent = (parseInt(split[0], 10) / parseInt(split[1], 10)) * 100
        return `
        .account-info-form-col-${colSize.replace('/', '\\/')} {
          flex: 1 1 ${percent}%;
        }
      `
      })
      .join('')}
    
    @media screen and (max-width: 991px) {
      lvc-button {
        width: 100%;
      }
    }

    @media only screen and (min-width: 992px) {
      .account-info-form {
        gap: 30px;
      }

      .account-info-form-row {
        flex-direction: row;
        gap: 15px;
      }
    }
  `
}
